import { ApolloCache } from "@apollo/client";
import { Intent, OverlayToaster, Position } from "@blueprintjs/core";
import { useUserDataContext } from "contexts/UserDataContext/UserDataContext";
import {
    GetClusterProjectRowsDocument,
    GetClusterProjectRowsQuery,
    GetClusterProjectRowsQueryVariables,
    useSetProjectFavoriteMutation
} from "generated/graphql";
import { segmentTrackFavoriteProject } from "in_queue/analytics";
import { useCluster } from "in_queue/contexts/ClusterContext";
import { useCallback, useMemo } from "react";
import { ADMIN_TYPE } from "types/roleType";

export interface FavoriteProjectsApi {
    readonly canToggleFavorite: boolean;
    readonly setFavorite: (projectId: string, isFavorited: boolean) => void;
}

type ConfigurationRow =
    GetClusterProjectRowsQuery["cluster_projects"][number]["project_configurations"][number];

const FAVORITE_ERROR_MESSAGE = "Couldn't update the project's favorite.";

const FavoriteProjectsToaster = OverlayToaster.create({
    position: Position.TOP
});

/**
 * Point the project's row at the saved configuration. Covers the first
 * favorite on a project, when the row had no configuration to update.
 */
export const withFavorite = (
    existing: GetClusterProjectRowsQuery,
    saved: ConfigurationRow
): GetClusterProjectRowsQuery => ({
    ...existing,
    cluster_projects: existing.cluster_projects.map((project) =>
        project.project_id === saved.project_id
            ? { ...project, project_configurations: [saved] }
            : project
    )
});

export const useFavoriteProjects = (): FavoriteProjectsApi => {
    const { id: clusterId } = useCluster();
    const { teamId, role } = useUserDataContext();

    const [setProjectFavorite] = useSetProjectFavoriteMutation({
        onError: (error) => {
            console.error(FAVORITE_ERROR_MESSAGE, error);
            FavoriteProjectsToaster.show({
                message: FAVORITE_ERROR_MESSAGE,
                intent: Intent.DANGER
            });
        },
        update: (cache: ApolloCache<unknown>, { data }) => {
            const saved = data?.insert_project_configurations_one;
            if (saved) {
                cache.updateQuery<
                    GetClusterProjectRowsQuery,
                    GetClusterProjectRowsQueryVariables
                >(
                    {
                        query: GetClusterProjectRowsDocument,
                        variables: { clusterId }
                    },
                    (existing) =>
                        existing === null
                            ? undefined
                            : withFavorite(existing, saved)
                );
            }
        }
    });

    const setFavorite = useCallback(
        (projectId: string, isFavorited: boolean): void => {
            void setProjectFavorite({
                variables: { teamId, clusterId, projectId, isFavorited },
                optimisticResponse: {
                    insert_project_configurations_one: {
                        __typename: "project_configurations",
                        team_id: teamId,
                        cluster_id: clusterId,
                        project_id: projectId,
                        is_favorited: isFavorited
                    }
                }
            });
            segmentTrackFavoriteProject({ projectId, isFavorited });
        },
        [setProjectFavorite, teamId, clusterId]
    );

    return useMemo(
        () => ({ canToggleFavorite: role === ADMIN_TYPE, setFavorite }),
        [role, setFavorite]
    );
};
